'use client';

import { useToast } from '@/components/ui/use-toast';
import { ErrorHandler } from '@/lib/ErrorHandler';
import {
  claimBannerPromo,
  isBannerGenerateCode,
  loginUrlForBannerClaim,
  needsLoginForBannerClaim,
  stashBannerClaim,
} from '@/lib/bannerPromo';
import { cdnLcpUrl } from '@/lib/lcpImage';
import { HomePageBanner, HomePageCategory } from '@/models/HomePage';
import { toRootAbsolutePath } from '@/models/ChannelLandingPage';
import { isFuture, parseISO } from 'date-fns';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState, type MouseEvent } from 'react';
import SmartImage from './SmartImage';
import { ScrollArea } from './ui/scroll-area';

const DESKTOP_MEDIA = '(min-width: 768px)';

function activeBanners(banners?: HomePageBanner[]) {
  return (banners || [])
    .filter((banner) => {
      if (!banner) return false;
      if (!banner.badgeEndTime) return true;
      try {
        return isFuture(parseISO(banner.badgeEndTime));
      } catch {
        return true;
      }
    })
    .slice(0, 5);
}

function GridBannerCard({
  banner,
  aspectClass,
  roundedClass,
  imageWidth,
  isPriority = false,
}: {
  banner: HomePageBanner;
  aspectClass: string;
  roundedClass: string;
  imageWidth: number;
  isPriority?: boolean;
}) {
  const [claiming, setClaiming] = useState(false);
  const { toast } = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const generateCode = isBannerGenerateCode(banner.generateCode);
  const href = toRootAbsolutePath(banner.url);
  const altText = banner.alt || banner.title || 'kampanya görseli';
  const desktopImage = banner.imageUrl || '';
  const mobileImage = banner.mobileImageUrl || desktopImage;
  const mobileSrc = cdnLcpUrl(mobileImage, imageWidth);
  const desktopSrc = cdnLcpUrl(desktopImage || mobileImage, imageWidth);

  const goToBannerUrl = () => {
    if (!href) return;
    if (/^https?:\/\//i.test(href)) {
      window.location.assign(href);
      return;
    }
    router.push(href);
  };

  const onBannerClick = async (event: MouseEvent) => {
    if (!generateCode || !banner.id) return;
    event.preventDefault();
    if (claiming) return;
    if (needsLoginForBannerClaim()) {
      stashBannerClaim({ id: banner.id, url: href }, pathname || '/');
      router.push(loginUrlForBannerClaim());
      return;
    }
    setClaiming(true);
    try {
      const code = await claimBannerPromo(banner.id);
      if (code) {
        toast({ description: `Kampanya kodunuz: ${code}` });
      } else {
        toast({ variant: 'destructive', description: 'Şu an kod verilemiyor' });
      }
      goToBannerUrl();
    } catch (error) {
      toast({
        variant: 'destructive',
        description: ErrorHandler.getMessage(error).message || 'Kod alınamadı',
      });
    } finally {
      setClaiming(false);
    }
  };

  const inner = (
    <div className={`relative w-full overflow-hidden ${aspectClass} ${roundedClass}`}>
      {mobileSrc ? (
        <picture>
          {desktopSrc ? <source media={DESKTOP_MEDIA} srcSet={desktopSrc} /> : null}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={mobileSrc}
            alt={altText}
            width={imageWidth}
            height={Math.round(imageWidth / 2)}
            decoding={isPriority ? 'sync' : 'async'}
            loading={isPriority ? 'eager' : 'lazy'}
            fetchPriority={isPriority ? 'high' : 'low'}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </picture>
      ) : null}
    </div>
  );

  const className = `block w-full${claiming ? ' pointer-events-none' : ''}`;

  if (generateCode && !href) {
    return (
      <button type="button" className={`${className} text-left`} onClick={onBannerClick}>
        {inner}
      </button>
    );
  }

  return (
    <Link href={href || '/'} prefetch={false} className={className} onClick={onBannerClick}>
      {inner}
    </Link>
  );
}

function CategoryItem({ category }: { category: HomePageCategory }) {
  return (
    <Link
      href={category.url || ''}
      prefetch={false}
      className="flex items-center gap-4 rounded-lg px-4 py-4 transition-colors duration-200 hover:bg-hzc_beige_300"
    >
      <div className="relative h-6 w-6 flex-shrink-0">
        <SmartImage
          src={category.iconUrl || ''}
          alt={`${category.title} - Kategorisi`}
          fill
          sizes="48px"
          quality={60}
          loading="lazy"
          className="rounded-lg object-cover"
          imageType="icon"
        />
      </div>
      <h2 className="min-w-0 flex-1 truncate text-sm font-medium text-hzc_purple_500">
        {category.title}
      </h2>
    </Link>
  );
}

export default function StaggeredBannerWithCategoryList({
  banners,
  categories = [],
  categoriesTitle,
  isPriority = false,
}: {
  banners?: HomePageBanner[];
  categories?: HomePageCategory[];
  categoriesTitle: string;
  isPriority?: boolean;
}) {
  const [slots, setSlots] = useState<HomePageBanner[]>(() => (banners || []).filter(Boolean).slice(0, 5));

  useEffect(() => {
    setSlots(activeBanners(banners));
  }, [banners]);

  if (slots.length === 0 && categories.length === 0) {
    return null;
  }

  const top = slots.slice(0, 2);
  const bottom = slots.slice(2, 5);

  return (
    <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-4 md:grid-cols-[1fr_minmax(220px,25%)]">
      <div>
        <div className="flex flex-col gap-3 px-4 md:hidden">
          {slots.map((banner, index) => (
            <GridBannerCard
              key={banner.id ?? index}
              banner={banner}
              aspectClass="aspect-[2/1]"
              roundedClass="rounded-xl"
              imageWidth={800}
              isPriority={isPriority && index === 0}
            />
          ))}
        </div>

        <div className="hidden flex-col gap-3 md:flex lg:gap-4">
          {top.length > 0 && (
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              {top.map((banner, index) => (
                <GridBannerCard
                  key={banner.id ?? index}
                  banner={banner}
                  aspectClass="aspect-[2/1]"
                  roundedClass="rounded-2xl"
                  imageWidth={800}
                  isPriority={isPriority && index === 0}
                />
              ))}
            </div>
          )}
          {bottom.length > 0 && (
            <div className="grid grid-cols-3 gap-3 lg:gap-4">
              {bottom.map((banner, index) => (
                <GridBannerCard
                  key={banner.id ?? `bottom-${index}`}
                  banner={banner}
                  aspectClass="aspect-[3/2]"
                  roundedClass="rounded-2xl"
                  imageWidth={600}
                  isPriority={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {categories.length > 0 && (
        <div className="relative hidden min-h-0 md:block">
          <div className="absolute inset-0 flex flex-col gap-2 rounded-2xl border bg-hzc_beige_400">
            <div className="shrink-0 px-2 pt-4 text-left text-xl font-semibold text-hzc_purple_500">
              {categoriesTitle}
            </div>
            <ScrollArea className="min-h-0 flex-1">
              <div className="relative flex flex-col pb-2">
                {categories.map((category) => (
                  <CategoryItem key={category.id} category={category} />
                ))}
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-hzc_beige_400 to-transparent" />
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </div>
  );
}
